import React, { createContext, useState, useEffect, useRef } from 'react';
import { IntlProvider } from 'react-intl';
import { setCurrentLanguage } from '@/helpers/Utils';

export const LangContext = createContext();
export const LANGUAGE_CHANGE_EVENT = 'app-language-changed';

export const notifyLanguageChange = (locale) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(LANGUAGE_CHANGE_EVENT, {
      detail: {
        locale,
        dir: locale === 'ar' ? 'rtl' : 'ltr',
        timestamp: Date.now(),
      },
    })
  );
};

// Lazy load translation files
const loadMessages = async (locale) => {
  if (locale === 'ar') {
    const module = await import(/* webpackChunkName: "lang-ar" */ '../lang/ar');
    return module.default;
  } else {
    const module = await import(/* webpackChunkName: "lang-en" */ '../lang/en');
    return module.default;
  }
};

const LangProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  // Set the initial language based on the body's dir attribute (ltr or rtl)
  const [locale, setLocale] = useState(() => {
    try {
      const stored =
        localStorage.getItem('PreferredLanguage') ||
        localStorage.getItem('currentLanguage');
      if (stored) {
        return String(stored).toLowerCase().startsWith('ar') ? 'ar' : 'en';
      }
    } catch {}
    return document.body.getAttribute('dir') === 'rtl' ? 'ar' : 'en';
  });

  const [messages, setMessages] = useState(null);

  const languageRequestRef = useRef(0);
  const isFirstLanguageLoad = useRef(true);

  const applyLanguage = (newLocale) => {
    const normalizedLocale = newLocale === 'ar' ? 'ar' : 'en';
    const requestId = languageRequestRef.current + 1;
    languageRequestRef.current = requestId;
    setIsLoading(true);

    loadMessages(normalizedLocale).then((msgs) => {
      if (languageRequestRef.current !== requestId) return;
      setLocale(normalizedLocale);
      setMessages(msgs);
      document.body.setAttribute('dir', normalizedLocale === 'ar' ? 'rtl' : 'ltr');
      document.documentElement.lang = normalizedLocale;
      document.documentElement.dir = normalizedLocale === 'ar' ? 'rtl' : 'ltr';
      try { setCurrentLanguage(normalizedLocale); } catch {}
      setIsLoading(false);
    });
  };

  // Load initial messages
  useEffect(() => {
    applyLanguage(locale);
  }, []);

  useEffect(() => {
    if (isLoading || !messages) return;
    // Skip the initial page load — only notify when the user explicitly switches language
    if (isFirstLanguageLoad.current) {
      isFirstLanguageLoad.current = false;
      return;
    }
    notifyLanguageChange(locale);
  }, [isLoading, locale, messages]);

  const switchLanguage = (newLocale) => {
    applyLanguage(newLocale);
  };
  // console.log(messages);

  // Only block on the very first load. Keeping children mounted while switching
  // language avoids unmounting the whole app (which looked like a full reload and
  // discarded unsaved state such as the open configuration drawer).
  if (!messages) {
    return null;
  }

  return (
    <LangContext.Provider value={{ locale, switchLanguage }}>
      <IntlProvider
        locale={locale}
        messages={messages}
        showWarningOnEmptyStrings
      >
        {children}
      </IntlProvider>
    </LangContext.Provider>
  );
};

export default LangProvider;
